// Fallback for the landing hero when the atlas can't be shown (not built
// yet, failed to load, or no WebGL): a light 2D-canvas constellation in the
// atlas palette. Pauses off-screen; draws a single frame for reduced motion.
import { CALM } from '../atlas/atlasShared';

const N = 110;
const LINK = 7000; // squared px distance under which two points are linked

export function startConstellation(canvas: HTMLCanvasElement): () => void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return () => {};
  const pts = Array.from({ length: N }, (_, i) => ({
    x: Math.random(),
    y: Math.random(),
    vx: (Math.random() - 0.5) * 2e-4,
    vy: (Math.random() - 0.5) * 2e-4,
    c: i % 5 === 0 ? CALM.actor : CALM.family,
    r: 1.1 + Math.random() * 2,
  }));
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  let visible = true;
  let raf = 0;
  let stopped = false;

  const draw = (move: boolean) => {
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);
    if (move) {
      for (const p of pts) {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > 1) p.vx *= -1;
        if (p.y < 0 || p.y > 1) p.vy *= -1;
      }
    }
    ctx.lineWidth = 0.7;
    ctx.strokeStyle = '#c79bff';
    for (let i = 0; i < N; i++) {
      for (let j = i + 1; j < N; j++) {
        const dx = (pts[i].x - pts[j].x) * w;
        const dy = (pts[i].y - pts[j].y) * h;
        const d = dx * dx + dy * dy;
        if (d >= LINK) continue;
        ctx.globalAlpha = 0.24 * (1 - d / LINK);
        ctx.beginPath();
        ctx.moveTo(pts[i].x * w, pts[i].y * h);
        ctx.lineTo(pts[j].x * w, pts[j].y * h);
        ctx.stroke();
      }
    }
    ctx.globalAlpha = 0.9;
    for (const p of pts) {
      ctx.fillStyle = p.c;
      ctx.beginPath();
      ctx.arc(p.x * w, p.y * h, p.r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  };

  const loop = () => {
    draw(true);
    raf = requestAnimationFrame(loop);
  };
  const kick = () => {
    cancelAnimationFrame(raf);
    if (stopped) return;
    if (visible && !document.hidden && !reduce.matches) raf = requestAnimationFrame(loop);
    else draw(false);
  };
  const io = new IntersectionObserver((es) => {
    visible = es[0]?.isIntersecting ?? true;
    kick();
  });
  io.observe(canvas);
  const onResize = () => { if (reduce.matches) draw(false); };
  window.addEventListener('resize', onResize);
  document.addEventListener('visibilitychange', kick);
  reduce.addEventListener('change', kick);
  kick();

  return () => {
    stopped = true;
    cancelAnimationFrame(raf);
    io.disconnect();
    window.removeEventListener('resize', onResize);
    document.removeEventListener('visibilitychange', kick);
    reduce.removeEventListener('change', kick);
  };
}
